/* eslint-disable no-nested-ternary */
import Favorite from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import Typography from '@mui/material/Typography';
import { colors } from '@startengine/design-system.theme.main';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'next/navigation';
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { FollowButtonStyled, HeartStyled, HeartFilledStyled } from './styles';
import useIsMobile from 'hooks/useIsMobile';
import useIsSignedIn from 'hooks/useIsSignedIn';
import useLoginModal from 'hooks/useLoginModal';
import useSnackbar from 'hooks/useSnackbar';
import { updateOfferingField } from 'reducers/offering/slice';
import { isFollowing, startFollowing, unFollow } from 'services/offerings';
import { trackEvent, trackSEEvent } from 'utils/analytics';

const FollowButton = () => {
  const following = useSelector((state) => state.offering.following);
  const {
    needToLogin,
    verifiedPrimaryUserId,
    loading: partnerLoading,
  } = useSelector((state) => state.partnerVerification);
  const user = useSelector((state) => state.user);

  const [renderLoginModal, setLoginModalOpen] = useLoginModal();

  const queryClient = useQueryClient();

  const offeringData = queryClient.getQueryData(['offeringData']);

  const params = useParams();
  const slug = offeringData?.slug || params?.slug;

  const primaryUserId = useSelector((state) => state.user.primaryUserId);
  const signedIn = useIsSignedIn();
  const dispatch = useDispatch();
  const [renderSnackbar, setSnackbar] = useSnackbar();
  const isMobile = useIsMobile();

  const { data } = useQuery({
    queryKey: ['isFollowing'],
    queryFn: () => isFollowing(primaryUserId, slug),
    retry: 0,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
    enabled: Boolean(signedIn),
  });

  useEffect(() => {
    if (data === true) {
      dispatch(
        updateOfferingField({
          key: 'following',
          value: true,
        }),
      );
    }

    if (data === false) {
      dispatch(
        updateOfferingField({
          key: 'following',
          value: false,
        }),
      );
    }
  }, [dispatch, data]);

  const followMutation = useMutation({
    mutationFn: ({ userId, offeringSlug } = {}) =>
      startFollowing(userId || primaryUserId, offeringSlug || slug),
    onSuccess: (data) => {
      dispatch(
        updateOfferingField({
          key: 'following',
          value: true,
        }),
      );

      trackSEEvent('tapped_follow_web', offeringData?.slug, user);

      trackEvent('clicked_follow', {
        offeringId: offeringData?.id,
        offeringName: offeringData?.name,
        offeringSlug: slug,
      });

      if (data?.status === 201) {
        const newFollowerPayload = {
          user_id: primaryUserId,
          email: user?.email,
          issuer_slug: slug,
        };
        trackEvent('new_follower', newFollowerPayload);
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'new_follower',
          ...newFollowerPayload,
        });
      }
    },
    onError: () => {
      setSnackbar(true);
      renderSnackbar({
        message: 'Unable to add to watchlist. Please try again.',
        duration: 3000,
        color: 'error.main',
      });
    },
  });

  const unFollowMutation = useMutation({
    mutationFn: () => unFollow(primaryUserId, slug),
    onSuccess: () => {
      dispatch(
        updateOfferingField({
          key: 'following',
          value: false,
        }),
      );

      trackSEEvent('tapped_unfollow_web', offeringData?.slug, user);

      // Track unfollow event with PostHog
      trackEvent('clicked_unfollow', {
        offeringId: offeringData?.id,
        offeringName: offeringData?.name,
        offeringSlug: slug,
      });
    },
    onError: () => {
      setSnackbar(true);
      renderSnackbar({
        message: 'Unable to remove from watchlist. Please try again.',
        duration: 3000,
        color: 'error.main',
      });
    },
  });

  const handleFollow = async () => {
    if (signedIn && !needToLogin && !partnerLoading) {
      if (following) {
        unFollowMutation.mutate();
      } else {
        followMutation.mutate();
        if (isMobile) {
          setSnackbar(true);
          renderSnackbar({
            message: `You'll stay up to date with ${offeringData?.name}`,
            duration: 3000,
            color: 'text.primary',
          });
        }
      }
    } else {
      setLoginModalOpen(true);
    }
  };

  return (
    <>
      {!isMobile ? (
        <FollowButtonStyled
          disableRipple
          data-cy="follow-desktop-button"
          onClick={handleFollow}
          color={following ? 'secondary' : 'success'}
          sx={{
            width: 204,
            pointerEvents: (followMutation.isPending || unFollowMutation.isPending) && 'none',
          }}
          variant="outlined"
          startIcon={
            followMutation.isPending || unFollowMutation.isPending ? (
              ''
            ) : following ? (
              <Favorite />
            ) : (
              <FavoriteBorderIcon />
            )
          }
          loading={followMutation.isPending || unFollowMutation.isPending}
        >
          <Typography variant="p2Bold">{following ? 'Watchlist' : 'Add to Watchlist'}</Typography>
        </FollowButtonStyled>
      ) : null}
      {isMobile &&
        (following ? (
          <HeartFilledStyled
            data-cy="follow-mobile-button"
            onClick={handleFollow}
            color={colors.white}
            sx={{
              pointerEvents: unFollowMutation.isPending && 'none',
            }}
          />
        ) : (
          <HeartStyled
            data-cy="follow-mobile-button"
            onClick={handleFollow}
            color={colors.white}
            sx={{
              pointerEvents: followMutation.isPending && 'none',
            }}
          />
        ))}
      {renderLoginModal({
        offering: offeringData,
        verifiedPrimaryUserId,
        onSuccessfulLogin: (response) => {
          const userPrimaryId = response?.primaryUserId || primaryUserId;
          if (userPrimaryId && slug) {
            followMutation.mutate({ userId: userPrimaryId, offeringSlug: slug });
          }
        },
      })}
    </>
  );
};

export default FollowButton;
